import { useContext, useCallback, useMemo } from 'react';
import { modeUIContext, showSuccessNotification,
    showDialog,
    DialogType,
    DialogResponse,
    DialogResponseCode,
    showErrorNotification,
    hideSlideOutPanel } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { Agent, AgentRole, ApiError, ProjectAgent, RitzAPI } from '@moderepo/mode-internal-apis';
import { selectProjectById,
    ritzDataContext,
    selectProjectAgents,
    fetchProjectAgents,
    selectLoggedInAgent,
    selectProjectAgent,
    fetchAgentProjects,
    removeProjectAgent } from '@moderepo/mode-data-state-ritz';
import { BaseCompAction, capitalizeAll, getErrorMessage, SlideOutComp, SlideOutCompProps } from '@moderepo/mode-app-components';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core';
import { useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';
import { AgentInfoContainer } from './AgentInfoContainer';

const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});

const findAgent = (agentList: readonly ProjectAgent[] | undefined, agentId: number | undefined) => {
    const result = agentList?.find((agent) => {
        return agent.agentId === agentId;
    });
    return result;
};

export interface AgentInfoPreviewContainerProps {
    readonly projectId: number;
    readonly agentId?: number;
}

/**
 * This is the container for the AgentInfoComp. It provides the logic for the AgentInfoComp which is a dumb component that does not
 * do anything other than displaying data. This container will take care of all the logic e.g. edit agent, etc...
 * @param props
 */

export const AgentInfoPreviewContainer = (props: AgentInfoPreviewContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const navigate = useNavigate();
    const classes = useStyle();


    const agents = selectProjectAgents(dataState, props.projectId);

    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, props.projectId, loggedInAgent.id) : undefined;
    const targetAgent = findAgent(agents, props.agentId);
    const isNewAgent = props.agentId === undefined;

    /**
     * Unselect the selected agent
     */
    const unselectAgent = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);

    const onNewAgentCanceled = useCallback(() => {
        unselectAgent();
    }, [unselectAgent]);



    const onNewAgentCreated = useCallback(() => {
        unselectAgent();
    }, [unselectAgent]);

    /**
     * use useMemo to create the the newUserSettings so that this doesn't get created on every re-render
     */
    const newAgentSettings = useMemo(() => {
        return {
            onNewAgentCreated,
            onNewAgentCanceled,
        };
    }, [onNewAgentCreated, onNewAgentCanceled]);


    /**
     * Resend activation email to the agent if the agent has not verified yet.
     */
    const onResendMailClicked = useCallback((agent: ProjectAgent) => {
        // Confirm before changing agent's role
        if (agent && !agent.verified) {
            uiDispatch(showDialog({
                type          : DialogType.CONFIRM,
                title         : 'dialog.confirm_resend_activate_agent.title',
                message       : 'dialog.confirm_resend_activate_agent.message',
                messageData   : { name: (agent.name ? agent.name : `#${agent.agentId}`) },
                positiveButton: 'label.yes',
                negativeButton: 'label.cancel',
                onClose       : async (response?: DialogResponse): Promise<void> => {
                    if (response && response.code === DialogResponseCode.POSITIVE) {
                        RitzAPI.getInstance().resendActivateEmail(props.projectId, agent.agentId, agent.email).then(() => {
                            uiDispatch(showSuccessNotification('notif.activate_email_sent_successfully'));
                        }).catch((error) => {
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                        });
                    }
                },
            }));
        }

    }, [uiDispatch, props.projectId]);

    const onRemoveAgentClicked = useCallback((agent: ProjectAgent, errorMessage?: string | undefined) => {
        // Confirm before removing agent
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'dialog.generic_confirm_remove.title',
            titleData     : { object_term: capitalizeAll(trans('label.agent')) },
            message       : 'dialog.generic_confirm_remove.message',
            messageData   : { object_name: (agent.name ? agent.name : `#${agent.agentId}`), object_term: 'label.agent' },
            positiveButton: 'label.remove',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : capitalizeAll(trans(agent.name ? 'label.name' : 'label.agent_id')),
                error          : errorMessage,
                placeholder    : 'input.remove_object_name.hint_text',
                translationData: {
                    object_term : 'label.agent',
                    object_field: agent.name ? 'label.name' : 'label.agent_id',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const userResponse = response.input ? response.input.trim() : undefined;

                    if (!userResponse) {
                        // User input is not provided
                        onRemoveAgentClicked(agent, 'error.input.generic.empty');
                    } else if (agent.name && userResponse !== agent.name) {
                        // user input does not match agentName
                        onRemoveAgentClicked(agent, 'error.input.generic.invalid');
                    } else if (!agent.name && Number(userResponse) !== agent.agentId) {
                        // user input does not match agentID
                        onRemoveAgentClicked(agent, 'error.input.generic.invalid');
                    } else {
                        dataDispatch(removeProjectAgent(props.projectId, agent.agentId)).then(() => {
                            uiDispatch(showSuccessNotification('notif.agent_removed_successfully'));
                            // if the agent removed themselves from the project, they can no longer
                            // have access to this project therefore we need to
                            // take them to the Ritz home page where they have to select another project.
                            if (projectAgent?.agentId === agent.agentId) {
                                // Reload the list of projects the logged in agent has access to
                                dataDispatch(fetchAgentProjects(projectAgent?.agentId)).catch(() => { /* ignore error */ });
                                RouteHelper.getInstance(navigate).gotoRitzHomePage();
                            } else {
                                dataDispatch(fetchProjectAgents(props.projectId)).catch(() => { /* ignore error */ });
                                unselectAgent();
                            }
                        }).catch(() => {
                            // do nothing
                        });
                    }
                }
            },
        }));
    }, [uiDispatch, trans, dataDispatch, props.projectId, projectAgent?.agentId, navigate, unselectAgent]);

    /**
     * Create an Array of primary actions for the Admin Agent
     * If an agent is not verified, have button to re-send verification email
     */
    const primaryActions = useMemo((): SlideOutCompProps['primaryActions'] => {
        if (!targetAgent?.verified && targetAgent && projectAgent?.role === AgentRole.ADMIN && !isNewAgent) {
            return [{
                label  : trans('dialog.confirm_resend_activate_agent.title'),
                onClick: () => {
                    onResendMailClicked(targetAgent);
                },
            }];
        }

        return undefined;
    }, [isNewAgent, onResendMailClicked, projectAgent?.role, targetAgent, trans]);


    /**
     * Create secondary actions for the Admin Agent
     */
    const secondaryActions = useMemo(() => {
        if (projectAgent?.role === AgentRole.ADMIN && targetAgent && !isNewAgent) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                label    : capitalizeAll(trans('label.remove_agent')),
                icon     : 'delete',
                onClick  : () => {
                    onRemoveAgentClicked(targetAgent, undefined);
                },
            }];
            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, isNewAgent, onRemoveAgentClicked, projectAgent?.role, targetAgent, trans]);

    /**
     * Create the content for the slide-out panel. The content will just going to be the AgentInfoContainer
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <AgentInfoContainer
                className="transparent"
                projectId={props.projectId}
                agentId={props.agentId}
                newAgentSettings={
                    isNewAgent ? newAgentSettings : undefined
                }
            />
        );
    }, [isNewAgent, newAgentSettings, props.agentId, props.projectId]);


    return (
        <div>
            <SlideOutComp
                primaryActions={primaryActions}
                secondaryActions={secondaryActions}
                compContent={slideOutCompContent}
                closable={!isNewAgent}
                contentContainerProps={{
                    className: 'slide-out-comp-container',
                }}
            />

        </div>
    );
};
